import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactMarkdown from 'react-markdown';
import EditIcon from '@mui/icons-material/Edit';
import CategoryIcon from '@mui/icons-material/Category';
import GroupIcon from '@mui/icons-material/Group';
import PublicIcon from '@mui/icons-material/Public';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ChatIcon from '@mui/icons-material/Chat';
import LayersIcon from '@mui/icons-material/Layers';
import BusinessIcon from '@mui/icons-material/Business';
import StorefrontIcon from '@mui/icons-material/Storefront';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../shared/components/PageHeader';
import { NewLineOfBizDialog } from '../../shared/components/NewLineOfBizDialog';
import { GenerateIdustriesDialog } from './GenerateIdustriesDialog';
import { GenerateCompetitorsDialog } from './GenerateCompetitorsDialog';
import { GenerateCategoriesDialog } from './GenerateCategoriesDialog';
import { GeneratePersonasDialog } from './GeneratePersonasDialog';
import { GeneratePromptsDialog } from '../prompts/GeneratePromptsDialog';
import { EditIdustriesDialog } from './EditIdustriesDialog';
import { useLineOfBiz } from '../../core/contexts/LineOfBizContext';
import { resources } from '../../core/services/resources';
import { flagFor } from '../../shared/utils/geo';
import type {
  AIModel,
  GeoLocation,
  LineOfBiz,
  LineOfBizBrand,
  LineOfBizCategory,
  LineOfBizPersona,
  LineOfBizStatus,
  ReportTemplate,
  ReportTemplatePrompt,
  ReportTemplatePromptSet,
} from '../../core/types/data-models';

const STATUS_LABEL: Record<LineOfBizStatus, string> = {
  ACTIVE: 'Active',
  DRAFT: 'Draft',
  DISABLED: 'Disabled',
};

const STATUS_COLOR: Record<LineOfBizStatus, 'success' | 'warning' | 'default'> = {
  ACTIVE: 'success',
  DRAFT: 'warning',
  DISABLED: 'default',
};

/**
 * Headline KPI card — big number plus a label. Used for the count-only
 * tiles (Prompts / Prompt sets) where rendering a list adds no value.
 */
function CountCard({
  title,
  icon,
  value,
  loading,
}: {
  title: string;
  icon: ReactNode;
  value: number;
  loading?: boolean;
}) {
  return (
    <Card sx={{ flex: '1 1 240px', minWidth: 240 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {loading ? '…' : value}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

/**
 * Generic list card used by the four tiles on the LOB dashboard. Renders
 * a header + count chip + an unordered list of `items` (strings). When
 * the list is empty we show an inline empty message so the card never
 * collapses to a useless empty rectangle.
 */
function ListCard({
  title,
  icon,
  items,
  loading,
  emptyMessage = 'Nothing yet',
}: {
  title: string;
  icon: ReactNode;
  items: ReactNode[];
  loading?: boolean;
  emptyMessage?: string;
}) {
  return (
    <Card sx={{ flex: '1 1 240px', minWidth: 240 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
          <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
          <Chip size="small" label={loading ? '…' : items.length} />
        </Stack>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {loading ? '…' : emptyMessage}
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {items.map((item, idx) => (
              <Typography key={idx} variant="body2" noWrap>
                {item}
              </Typography>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Dashboard view scoped to the active LineOfBiz. Surfaces the building
 * blocks that show up in this LOB's reports — categories and personas
 * tagged to the LOB, plus the geo and AI-model coverage derived from
 * the LOB's report templates (via `ReportTemplatePrompt` /
 * `ReportTemplatePromptSet` joins).
 */
export function LineOfBizDashboardPage() {
  const { activeLineOfBizId } = useLineOfBiz();
  // Edit-dialog state lives at page scope (not on the card) so the
  // dialog renders once and survives card re-mounts.
  const [editOpen, setEditOpen] = useState(false);
  // Which onboarding "Generate" dialog is open (null = none). Each
  // incomplete step opens its matching generator.
  const [activeGenerate, setActiveGenerate] = useState<
    null | 'industries' | 'competitors' | 'categories' | 'personas' | 'prompts'
  >(null);
  const [editIdustriesOpen, setEditIdustriesOpen] = useState(false);

  // The LOB itself — only needed for the page title. Skips the request
  // when no LOB is selected so we don't fire a 404-bound GET.
  const lob = useQuery({
    queryKey: ['lines-of-business', activeLineOfBizId],
    queryFn: () => resources.linesOfBusiness.get(activeLineOfBizId as number),
    enabled: activeLineOfBizId != null,
  });

  // Categories and personas are already LOB-scoped at the row level, so
  // a simple filtered list does the job.
  const categories = useQuery({
    queryKey: ['line-of-biz-categories', { lineOfBizId: activeLineOfBizId }],
    queryFn: () =>
      resources.lineOfBizCategories.list({
        pageSize: 500,
        sort: 'name',
        filter: { lineOfBizId: activeLineOfBizId as number },
      }),
    enabled: activeLineOfBizId != null,
  });
  const personas = useQuery({
    queryKey: ['line-of-biz-personas', { lineOfBizId: activeLineOfBizId }],
    queryFn: () =>
      resources.lineOfBizPersonas.list({
        pageSize: 500,
        sort: 'name',
        filter: { lineOfBizId: activeLineOfBizId as number },
      }),
    enabled: activeLineOfBizId != null,
  });
  // Brands attached to this LOB — junction rows with `?include=brand`
  // so we get the brand name without a second round-trip.
  const lobBrands = useQuery({
    queryKey: ['line-of-biz-brands', { lineOfBizId: activeLineOfBizId }],
    queryFn: () =>
      resources
        .lineOfBizBrandsFor(activeLineOfBizId as number)
        .list({ pageSize: 500, include: ['tenantBrand'] }),
    enabled: activeLineOfBizId != null,
  });

  // Organizations catalog — used to resolve the parent organization name
  // for each brand row on the Competitors card (the junction's `brand`
  // include only carries `organizationId`, not the org name, because the
  // backend `allowedIncludes` doesn't walk to grandchildren).
  const orgsCatalog = useQuery({
    queryKey: ['organizations', 'select'],
    queryFn: () => resources.organizations.list({ pageSize: 500, sort: 'name' }),
    enabled: activeLineOfBizId != null,
  });

  // To compute geo/AI-model coverage we need:
  //   1. The LOB's report templates → their ids
  //   2. Every `ReportTemplatePrompt` / `ReportTemplatePromptSet` row
  //      that points at one of those template ids → their array
  //      columns hold the geo and AI-model coverage values.
  // We fetch the joins tenant-wide and filter client-side; the row
  // count for a single tenant stays small enough that this is cheaper
  // than N filtered requests (one per template).
  const reportTemplates = useQuery({
    queryKey: ['report-templates', 'select'],
    queryFn: () => resources.reportTemplates.list({ pageSize: 500, sort: 'name' }),
  });
  const rtPrompts = useQuery({
    queryKey: ['report-template-prompts', 'all'],
    queryFn: () => resources.reportTemplatePrompts.list({ pageSize: 1000 }),
  });
  const rtPromptSets = useQuery({
    queryKey: ['report-template-prompt-sets', 'all'],
    queryFn: () => resources.reportTemplatePromptSets.list({ pageSize: 1000 }),
  });

  // Lookup catalogs — turn ids → display names. Shared cache keys with
  // the rest of the app so this page usually doesn't trigger an extra
  // fetch.
  const geoCatalog = useQuery({
    queryKey: ['geo-locations', 'select'],
    queryFn: () => resources.geoLocations.list({ pageSize: 500, sort: 'name' }),
  });
  const aiModelCatalog = useQuery({
    queryKey: ['ai-models', 'select'],
    queryFn: () => resources.aiModels.list({ pageSize: 200, sort: 'aiEngine' }),
  });

  // Shared LOB catalog — used to resolve the parent's display name on
  // the new Details card. Same cache key the sidebar's switcher
  // populates, so this typically hits without a network round-trip.
  const lobCatalog = useQuery({
    queryKey: ['lines-of-business', 'select'],
    queryFn: () => resources.linesOfBusiness.list({ pageSize: 200, sort: 'name' }),
  });

  // "Has prompts?" — `Prompt` is now LOB-scoped, so a single page-size=1
  // probe filtered by lineOfBizId is enough to know whether the LOB
  // has any prompts (page.total reveals the count without us paging
  // through the full list).
  const promptsProbe = useQuery({
    queryKey: ['prompts', 'lob-probe', { lineOfBizId: activeLineOfBizId }],
    queryFn: () =>
      resources.prompts.list({
        pageSize: 1,
        filter: { lineOfBizId: activeLineOfBizId as number },
      }),
    enabled: activeLineOfBizId != null,
  });

  // "Has report instances?" — instances aren't LOB-scoped directly; we
  // ask the server for any instance whose reportTemplateId is one of
  // this LOB's templates. Skipped when the LOB has no templates (the
  // empty `in:` filter would be ill-formed and the answer is trivially
  // "no" anyway).
  const templateIdsList = useMemo(
    () =>
      ((reportTemplates.data?.data as ReportTemplate[] | undefined) ?? [])
        .filter((t) => t.lineOfBizId === activeLineOfBizId)
        .map((t) => t.id),
    [reportTemplates.data, activeLineOfBizId],
  );
  const instancesProbe = useQuery({
    queryKey: [
      'report-instances',
      'lob-probe',
      { lineOfBizId: activeLineOfBizId, templateIds: templateIdsList },
    ],
    queryFn: () =>
      resources.reportInstances.list({
        pageSize: 1,
        filter: { reportTemplateId: templateIdsList },
      }),
    enabled: activeLineOfBizId != null && templateIdsList.length > 0,
  });

  // Build a Set of template ids that belong to the active LOB. Used to
  // filter the (tenant-wide) join rows down to only those rolling up
  // to this LOB.
  const templateIdsForLob = useMemo(() => {
    const ids = new Set<number>();
    if (activeLineOfBizId == null) return ids;
    for (const t of (reportTemplates.data?.data as ReportTemplate[] | undefined) ?? []) {
      if (t.lineOfBizId === activeLineOfBizId) ids.add(t.id);
    }
    return ids;
  }, [reportTemplates.data, activeLineOfBizId]);

  // Distinct geo ids used by any prompt/prompt-set attached to one of
  // this LOB's templates. `Set` makes the de-dup straightforward.
  const geoIdsInUse = useMemo(() => {
    const ids = new Set<number>();
    const promptRows = (rtPrompts.data?.data as ReportTemplatePrompt[] | undefined) ?? [];
    for (const r of promptRows) {
      if (!templateIdsForLob.has(r.reportTemplateId)) continue;
      for (const id of r.geoLocationIds ?? []) ids.add(id);
    }
    const setRows = (rtPromptSets.data?.data as ReportTemplatePromptSet[] | undefined) ?? [];
    for (const r of setRows) {
      if (!templateIdsForLob.has(r.reportTemplateId)) continue;
      for (const id of r.geoLocationIds ?? []) ids.add(id);
    }
    return ids;
  }, [rtPrompts.data, rtPromptSets.data, templateIdsForLob]);

  // Same for AI models. `aiModelIds` is a Postgres int[] of `AIModel.id`
  // refs, matched against the catalog's numeric id.
  const aiModelIdsInUse = useMemo(() => {
    const ids = new Set<number>();
    const promptRows = (rtPrompts.data?.data as ReportTemplatePrompt[] | undefined) ?? [];
    for (const r of promptRows) {
      if (!templateIdsForLob.has(r.reportTemplateId)) continue;
      for (const id of r.aiModelIds ?? []) ids.add(id);
    }
    const setRows = (rtPromptSets.data?.data as ReportTemplatePromptSet[] | undefined) ?? [];
    for (const r of setRows) {
      if (!templateIdsForLob.has(r.reportTemplateId)) continue;
      for (const id of r.aiModelIds ?? []) ids.add(id);
    }
    return ids;
  }, [rtPrompts.data, rtPromptSets.data, templateIdsForLob]);

  // Resolve geo ids → "🇺🇸 United States" entries, sorted by name so the
  // list reads predictably. Unknown ids fall back to `#<id>`.
  const geoItems = useMemo(() => {
    const byId = new Map<number, GeoLocation>();
    for (const g of (geoCatalog.data?.data as GeoLocation[] | undefined) ?? []) byId.set(g.id, g);
    const out: { code: string; name: string }[] = [];
    for (const id of geoIdsInUse) {
      const g = byId.get(id);
      if (g) out.push({ code: g.code, name: g.name });
      else out.push({ code: '?', name: `#${id}` });
    }
    out.sort((a, b) => a.name.localeCompare(b.name));
    return out.map((g) => `${flagFor(g.code)} ${g.name}`);
  }, [geoIdsInUse, geoCatalog.data]);

  // Resolve AI-model ids → "name (engine)" entries. Catalog misses fall
  // back to `#<id>` so the row still renders.
  const aiModelItems = useMemo(() => {
    const byId = new Map<number, AIModel>();
    for (const m of (aiModelCatalog.data?.data as AIModel[] | undefined) ?? []) {
      byId.set(m.id, m);
    }
    const out: string[] = [];
    for (const id of aiModelIdsInUse) {
      const m = byId.get(id);
      out.push(m ? `${m.name} (${m.aiEngine})` : `#${id}`);
    }
    out.sort((a, b) => a.localeCompare(b));
    return out;
  }, [aiModelIdsInUse, aiModelCatalog.data]);

  // The LOB's own `supportedGeoLocationIds` / `supportedAiModelIds`
  // arrays (set via the LOB edit dialog) — resolved to display labels
  // for the Details card. These differ from the `geoIdsInUse` /
  // `aiModelIdsInUse` aggregates above, which derive from the LOB's
  // report-template prompts; the new fields are the LOB's *declared*
  // supported list, independent of any prompt-level overrides.
  const supportedGeoItems = useMemo(() => {
    const byId = new Map<number, GeoLocation>();
    for (const g of (geoCatalog.data?.data as GeoLocation[] | undefined) ?? []) byId.set(g.id, g);
    const out: { code: string; name: string }[] = [];
    for (const id of lob.data?.supportedGeoLocationIds ?? []) {
      const g = byId.get(id);
      if (g) out.push({ code: g.code, name: g.name });
      else out.push({ code: '?', name: `#${id}` });
    }
    out.sort((a, b) => a.name.localeCompare(b.name));
    return out.map((g) => `${flagFor(g.code)} ${g.name}`);
  }, [lob.data?.supportedGeoLocationIds, geoCatalog.data]);

  const supportedAiModelItems = useMemo(() => {
    // The LOB stores AIModel ids (not identifiers) — different from
    // the prompt-level overrides used by `aiModelItems`.
    const byId = new Map<number, AIModel>();
    for (const m of (aiModelCatalog.data?.data as AIModel[] | undefined) ?? []) byId.set(m.id, m);
    const out: string[] = [];
    for (const id of lob.data?.supportedAiModelIds ?? []) {
      const m = byId.get(id);
      out.push(m ? `${m.name} (${m.aiEngine})` : `#${id}`);
    }
    out.sort((a, b) => a.localeCompare(b));
    return out;
  }, [lob.data?.supportedAiModelIds, aiModelCatalog.data]);

  const supportedLanguagesItems = useMemo(() => {
    const codes = lob.data?.supportedLanguages ?? [];
    return [...codes].sort((a, b) => a.localeCompare(b));
  }, [lob.data?.supportedLanguages]);

  // Distinct prompts attached to any of this LOB's templates — same
  // join rows as the geo/AI-model coverage above, just deduped on a
  // different column. `promptCount` is the headline number on the new
  // KPI row.
  const promptCount = useMemo(() => {
    const ids = new Set<number>();
    const rows = (rtPrompts.data?.data as ReportTemplatePrompt[] | undefined) ?? [];
    for (const r of rows) {
      if (!templateIdsForLob.has(r.reportTemplateId)) continue;
      ids.add(r.promptId);
    }
    return ids.size;
  }, [rtPrompts.data, templateIdsForLob]);

  // Distinct prompt-sets attached to any of this LOB's templates.
  const promptSetCount = useMemo(() => {
    const ids = new Set<number>();
    const rows = (rtPromptSets.data?.data as ReportTemplatePromptSet[] | undefined) ?? [];
    for (const r of rows) {
      if (!templateIdsForLob.has(r.reportTemplateId)) continue;
      ids.add(r.promptSetId);
    }
    return ids.size;
  }, [rtPromptSets.data, templateIdsForLob]);

  const categoryItems = useMemo(
    () =>
      ((categories.data?.data as LineOfBizCategory[] | undefined) ?? []).map((c) => c.name),
    [categories.data],
  );
  const personaItems = useMemo(
    () =>
      ((personas.data?.data as LineOfBizPersona[] | undefined) ?? []).map((p) => p.name),
    [personas.data],
  );
  // Brands joined with `associationType = 'COMPETITOR'` — feeds the
  // Competitors card on the same row as Categories/Personas. Rendered
  // as "Organization › Brand" so the user can see which company a
  // competitor brand sits under at a glance. Sort key is the formatted
  // string so the list groups by organization.
  const competitorItems = useMemo<ReactNode[]>(() => {
    const orgById = new Map<number, string>();
    for (const o of orgsCatalog.data?.data ?? []) orgById.set(o.id, o.name);
    return ((lobBrands.data?.data as LineOfBizBrand[] | undefined) ?? [])
      .filter((r) => r.associationType === 'COMPETITOR')
      .map((r) => {
        const brandName = r.tenantBrand?.name ?? `#${r.tenantBrandId}`;
        const orgName =
          r.tenantBrand?.organizationId != null
            ? orgById.get(r.tenantBrand.organizationId)
            : undefined;
        return { orgName, brandName };
      })
      .sort((a, b) => {
        const oa = a.orgName ?? '';
        const ob = b.orgName ?? '';
        const byOrg = oa.localeCompare(ob);
        return byOrg !== 0 ? byOrg : a.brandName.localeCompare(b.brandName);
      })
      .map(({ orgName, brandName }, idx) =>
        orgName ? (
          <Box
            key={idx}
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}
          >
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {orgName}
            </Box>
            <Box component="span" sx={{ color: 'text.disabled' }}>
              ›
            </Box>
            <Box component="span">{brandName}</Box>
          </Box>
        ) : (
          brandName
        ),
      );
  }, [lobBrands.data, orgsCatalog.data]);

  // Resolve the parent LOB name for the Details card. Falls back to
  // `#<id>` while the catalog is loading or for ids outside the catalog
  // window (parent should always be in the same tenant, so this is a
  // belt-and-braces fallback).
  const parentName = useMemo(() => {
    const parentId = lob.data?.parentId;
    if (parentId == null) return null;
    const list = (lobCatalog.data?.data as LineOfBiz[] | undefined) ?? [];
    return list.find((l) => l.id === parentId)?.name ?? `#${parentId}`;
  }, [lob.data, lobCatalog.data]);

  if (activeLineOfBizId == null) {
    return (
      <Box>
        <PageHeader title="LOB Dashboard" description="Select a line of business from the sidebar to see its dashboard." />
      </Box>
    );
  }

  // Onboarding step model — order matches the user's spec. Each step
  // is "done" when the corresponding probe returns at least one row.
  // We use the page metadata (`page.total`) when the probe is page-1-of-1
  // so we never have to materialise the full list just to count.
  // Onboarding step descriptors. `count: undefined` means the step has
  // no countable subject (e.g. "Industries defined" is a boolean signal
  // on the LOB row itself).
  const categoriesCount =
    ((categories.data?.data as LineOfBizCategory[] | undefined) ?? []).length;
  const personasCount =
    ((personas.data?.data as LineOfBizPersona[] | undefined) ?? []).length;
  const promptsCount = promptsProbe.data?.page.total ?? 0;
  const reportTemplatesCount = templateIdsList.length;
  const reportInstancesCount = instancesProbe.data?.page.total ?? 0;
  const competitorsCount = competitorItems.length;

  // Order matters — this is the user-visible onboarding sequence. The
  // first incomplete row also picks up the inline "Generate" button.
  // `generate` resolves to the step's wired action; null means the
  // affordance is hidden even if the row is incomplete.
  const steps: {
    label: string;
    done: boolean;
    count?: number;
    generate?: (() => void) | null;
  }[] = [
    {
      label: 'Industries defined',
      done: (lob.data?.industries ?? '').trim().length > 0,
      generate: () => setActiveGenerate('industries'),
    },
    {
      label: 'Has competitors',
      done: competitorsCount > 0,
      count: competitorsCount,
      generate: () => setActiveGenerate('competitors'),
    },
    {
      label: 'Has categories',
      done: categoriesCount > 0,
      count: categoriesCount,
      generate: () => setActiveGenerate('categories'),
    },
    {
      label: 'Has personas',
      done: personasCount > 0,
      count: personasCount,
      generate: () => setActiveGenerate('personas'),
    },
    {
      label: 'Has prompts',
      done: promptsCount > 0,
      count: promptsCount,
      generate: () => setActiveGenerate('prompts'),
    },
    {
      label: 'Has report templates',
      done: reportTemplatesCount > 0,
      count: reportTemplatesCount,
      generate: null,
    },
    {
      label: 'Has report instances',
      done: reportInstancesCount > 0,
      count: reportInstancesCount,
      generate: null,
    },
  ];

  return (
    <Box>
      {/* Description intentionally omitted here — it's shown in the
          details card below, so repeating it under the title is redundant. */}
      <PageHeader
        title={lob.data?.name ?? `Line of business #${activeLineOfBizId}`}
      />

      {/* Row 0: LOB details + onboarding progress. */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Card sx={{ flex: '1 1 360px', minWidth: 320 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
              <Box sx={{ color: 'primary.main', display: 'flex' }}>
                <BusinessIcon />
              </Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Line of business
              </Typography>
              {/* Status sits inline with the title so the card content
                  starts directly with the LOB attributes. */}
              {lob.data?.status && (
                <Chip
                  size="small"
                  label={STATUS_LABEL[lob.data.status]}
                  color={STATUS_COLOR[lob.data.status]}
                  variant={lob.data.status === 'DISABLED' ? 'outlined' : 'filled'}
                />
              )}
              <Box sx={{ flexGrow: 1 }} />
              <Tooltip title="Edit line of business">
                <span>
                  <IconButton
                    size="small"
                    disabled={!lob.data}
                    onClick={() => setEditOpen(true)}
                    aria-label="edit line of business"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body2">
                  {lob.data?.description || (
                    <em style={{ opacity: 0.6 }}>No description</em>
                  )}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Website
                </Typography>
                <Typography variant="body2">
                  {lob.data?.website ? (
                    <a
                      href={lob.data.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'inherit' }}
                    >
                      {lob.data.website}
                    </a>
                  ) : (
                    <em style={{ opacity: 0.6 }}>Not set</em>
                  )}
                </Typography>
              </Box>
              {/* Industries sub-card. Empty state stays inline with the
                  Analyze CTA (no value to collapse). When the LOB has
                  Markdown stored, the body becomes a collapsible
                  Accordion (collapsed by default) with an edit pencil
                  in the summary row. */}
              {(lob.data?.industries ?? '').trim().length === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, backgroundColor: 'background.default' }}
                >
                  {/* Empty state: the analyzer action lives on the
                      Onboarding progress card. We keep the edit
                      pencil here so a user can paste / type the
                      Markdown manually via the same dialog the
                      filled state uses. */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Industries
                      </Typography>
                      <Typography variant="body2">
                        <em style={{ opacity: 0.6 }}>Not set yet</em>
                      </Typography>
                    </Box>
                    <Tooltip title="Edit Industries Markdown">
                      <IconButton
                        size="small"
                        disabled={activeLineOfBizId == null}
                        onClick={() => setEditIdustriesOpen(true)}
                        aria-label="edit industries"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>
              ) : (
                <Accordion
                  disableGutters
                  elevation={0}
                  // Collapsed by default — long Markdown shouldn't
                  // dominate the card; the user expands when they
                  // want to read or edit.
                  defaultExpanded={false}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'background.default',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ flexGrow: 1, pr: 1 }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Industries
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      <Tooltip title="Edit Industries Markdown">
                        <IconButton
                          size="small"
                          // Stop propagation so opening the editor
                          // doesn't also toggle the accordion.
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditIdustriesOpen(true);
                          }}
                          aria-label="edit industries"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    {/* Industries is stored as Markdown (## Industry /
                        - Segment - description). Render it natively
                        with react-markdown; the wrapper styles tighten
                        spacing so it reads like an inline summary, not
                        a marketing page. */}
                    <Box
                      sx={{
                        '& h2': {
                          fontSize: '0.95rem',
                          fontWeight: 700,
                          mt: 1.5,
                          mb: 0.5,
                          '&:first-of-type': { mt: 0 },
                        },
                        '& ul': { my: 0.5, pl: 3 },
                        '& li': { mb: 0.25 },
                        '& p': { my: 0.5 },
                        fontSize: '0.875rem',
                        lineHeight: 1.5,
                      }}
                    >
                      <ReactMarkdown>{lob.data?.industries ?? ''}</ReactMarkdown>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Brands
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {(() => {
                    // Owner brands only — competitors and other roles
                    // are rendered in dedicated cards (see Row 1).
                    const rows = (
                      (lobBrands.data?.data as LineOfBizBrand[] | undefined) ?? []
                    ).filter((r) => r.associationType === 'OWNER');
                    if (lobBrands.isLoading) {
                      return (
                        <Typography variant="body2" color="text.secondary">
                          …
                        </Typography>
                      );
                    }
                    if (rows.length === 0) {
                      return (
                        <Typography variant="body2">
                          <em style={{ opacity: 0.6 }}>No brands attached</em>
                        </Typography>
                      );
                    }
                    return (
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {rows
                          .map((r) => r.tenantBrand?.name ?? `#${r.tenantBrandId}`)
                          .sort((a, b) => a.localeCompare(b))
                          .map((name) => (
                            <Chip
                              key={name}
                              size="small"
                              icon={<StorefrontIcon fontSize="small" />}
                              label={name}
                              variant="outlined"
                            />
                          ))}
                      </Stack>
                    );
                  })()}
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Supported geo locations
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {supportedGeoItems.length === 0 ? (
                    <Typography variant="body2">
                      <em style={{ opacity: 0.6 }}>None set</em>
                    </Typography>
                  ) : (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {supportedGeoItems.map((label) => (
                        <Chip
                          key={label}
                          size="small"
                          label={label}
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Supported AI models
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {supportedAiModelItems.length === 0 ? (
                    <Typography variant="body2">
                      <em style={{ opacity: 0.6 }}>None set</em>
                    </Typography>
                  ) : (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {supportedAiModelItems.map((label) => (
                        <Chip
                          key={label}
                          size="small"
                          icon={<SmartToyIcon fontSize="small" />}
                          label={label}
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Supported languages
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {supportedLanguagesItems.length === 0 ? (
                    <Typography variant="body2">
                      <em style={{ opacity: 0.6 }}>None set</em>
                    </Typography>
                  ) : (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {supportedLanguagesItems.map((code) => (
                        <Chip
                          key={code}
                          size="small"
                          label={code}
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Parent line of business
                </Typography>
                <Typography variant="body2">
                  {parentName ?? <em style={{ opacity: 0.6 }}>None (root LOB)</em>}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 360px', minWidth: 320 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
              <Box sx={{ color: 'primary.main', display: 'flex' }}>
                <ChecklistIcon />
              </Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
                Onboarding progress
              </Typography>
              <Chip
                size="small"
                color="success"
                variant={steps.every((s) => s.done) ? 'filled' : 'outlined'}
                label={`${steps.filter((s) => s.done).length} / ${steps.length}`}
              />
            </Stack>
            <Stack spacing={0.75}>
              {steps.map((s) => {
                // Every incomplete row that has a wired generator shows a
                // "Generate" button. Steps with `generate: null` (report
                // templates / instances — no LLM generator) stay quiet.
                const showGenerate = !s.done && s.generate != null;
                return (
                  <Stack
                    key={s.label}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                  >
                    {s.done ? (
                      <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
                    ) : (
                      <RadioButtonUncheckedIcon
                        fontSize="small"
                        sx={{ color: 'text.disabled' }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        flexGrow: 1,
                        color: s.done ? 'success.main' : 'text.secondary',
                        fontWeight: s.done ? 600 : 400,
                      }}
                    >
                      {s.label}
                    </Typography>
                    {showGenerate && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AutoAwesomeIcon fontSize="small" />}
                        aria-label={`generate ${s.label}`}
                        onClick={() => s.generate?.()}
                        sx={{ py: 0, minWidth: 0, whiteSpace: 'nowrap' }}
                      >
                        Generate
                      </Button>
                    )}
                    {s.count != null && (
                      <Chip
                        size="small"
                        label={s.count}
                        variant="outlined"
                        sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 12 } }}
                      />
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* Row 1: LOB-tagged catalogs (Competitors, Categories, Personas). */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {/* Competitors — brands attached to this LOB whose junction
            row carries `associationType = 'COMPETITOR'`. */}
        <ListCard
          title="Competitors"
          icon={<EmojiEventsIcon />}
          items={competitorItems}
          loading={lobBrands.isLoading || orgsCatalog.isLoading}
          emptyMessage="No competitors tracked yet"
        />
        <ListCard
          title="Categories"
          icon={<CategoryIcon />}
          items={categoryItems}
          loading={categories.isLoading}
          emptyMessage="No categories tagged to this line of business"
        />
        <ListCard
          title="Personas"
          icon={<GroupIcon />}
          items={personaItems}
          loading={personas.isLoading}
          emptyMessage="No personas tagged to this line of business"
        />
      </Stack>

      {/* Row 2: coverage derived from the LOB's report templates. */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <ListCard
          title="Geo locations"
          icon={<PublicIcon />}
          items={geoItems}
          loading={
            geoCatalog.isLoading ||
            reportTemplates.isLoading ||
            rtPrompts.isLoading ||
            rtPromptSets.isLoading
          }
          emptyMessage="No geos used by this line of business' reports"
        />
        <ListCard
          title="AI models"
          icon={<SmartToyIcon />}
          items={aiModelItems}
          loading={
            aiModelCatalog.isLoading ||
            reportTemplates.isLoading ||
            rtPrompts.isLoading ||
            rtPromptSets.isLoading
          }
          emptyMessage="No AI models used by this line of business' reports"
        />
      </Stack>

      {/* Row 3: KPI tiles for prompt and prompt-set coverage. */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <CountCard
          title="Prompts"
          icon={<ChatIcon />}
          value={promptCount}
          loading={reportTemplates.isLoading || rtPrompts.isLoading}
        />
        <CountCard
          title="Prompt sets"
          icon={<LayersIcon />}
          value={promptSetCount}
          loading={reportTemplates.isLoading || rtPromptSets.isLoading}
        />
      </Stack>

      {/* Shared create/edit dialog — opened in edit mode from the
          Details card's pencil. The same dialog component backs the
          sidebar's "New line of business" entry. */}
      <NewLineOfBizDialog
        open={editOpen}
        editing={lob.data ?? null}
        onClose={() => setEditOpen(false)}
        onSaved={() => setEditOpen(false)}
      />

      <GenerateIdustriesDialog
        open={activeGenerate === 'industries'}
        lineOfBizId={activeLineOfBizId}
        onClose={() => setActiveGenerate(null)}
      />
      <GenerateCompetitorsDialog
        open={activeGenerate === 'competitors'}
        lineOfBizId={activeLineOfBizId}
        onClose={() => setActiveGenerate(null)}
      />
      <GenerateCategoriesDialog
        open={activeGenerate === 'categories'}
        lineOfBizId={activeLineOfBizId}
        onClose={() => setActiveGenerate(null)}
      />
      <GeneratePersonasDialog
        open={activeGenerate === 'personas'}
        lineOfBizId={activeLineOfBizId}
        onClose={() => setActiveGenerate(null)}
      />
      <GeneratePromptsDialog
        open={activeGenerate === 'prompts'}
        lineOfBizId={activeLineOfBizId}
        onClose={() => setActiveGenerate(null)}
      />

      <EditIdustriesDialog
        open={editIdustriesOpen}
        lineOfBizId={activeLineOfBizId}
        initialValue={lob.data?.industries ?? ''}
        onClose={() => setEditIdustriesOpen(false)}
      />
    </Box>
  );
}
